import React, { useMemo, useState } from 'react'
import { DatePicker, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import moment from 'moment'
import { Expaned, Row } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import { $post } from '@/api'
import { errorCallBack } from '@/utils'
import DataDateSelect, { scopeTypeMap } from '../components/data-date-select/data-date-select'
import DataViewChart from './data-view-chart'
import { getCombinedFinanceOverview, getFinanceOverviewTotalIncome, FinanceOverviewMetrics } from './finance-overview'

import './data-view.scss'

interface FinanceOverviewRow extends FinanceOverviewMetrics {
    date: string
}

// 数据概览接口 scope 映射：DataDateSelect 索引 [日, 周, 月, 年] -> 后端 [1, 4, 2, 3]
const SCOPE_TO_API = [1, 4, 2, 3]

export const getDateByScope = (scope: number) => {
    let from: string | undefined
    let to: string | undefined
    switch (scope) {
        case 0:
            from = dayjs().subtract(14, 'day').format('YYYY-MM-DD')
            to = dayjs().format('YYYY-MM-DD')
            break
        case 1:
            from = dayjs().startOf('week').subtract(7, 'week').add(1, 'd').format('YYYY-MM-DD')
            to = dayjs().endOf('week').add(1, 'd').format('YYYY-MM-DD')
            break
        case 2:
            from = dayjs().startOf('month').subtract(5, 'month').format('YYYY-MM-DD')
            to = dayjs().endOf('month').format('YYYY-MM-DD')
            break
        case 3:
            from = dayjs().startOf('year').subtract(2, 'year').format('YYYY-MM-DD')
            to = dayjs().endOf('year').format('YYYY-MM-DD')
            break
        default:
            break
    }

    return [from, to]
}

export const formatDataViewDate = (value: string, scope: number) => {
    if (!value) return '-'
    if (scope === 2) return value.split('-').slice(0, -1).join('-')
    if (scope === 3) return value.substring(0, 4)

    return value
}

const DEFAULT_TIP = '当前展示近15日走势'

const formatMoney = (value?: number | string) => {
    if (value == null || value === '') return '0.00'

    return Number(value).toFixed(2)
}

const DataView: React.FC = () => {
    const cls = useBemClass('data-view')
    const [scope, setScope] = useState<number>(0)
    const [date, setDate] = useState<(string | undefined)[]>(getDateByScope(0))
    const [tip, setTip] = useState<string>(DEFAULT_TIP)
    const [showTip, setShowTip] = useState<boolean>(true)

    const req = useRequest<FinanceOverviewRow[]>(
        () =>
            $post({
                url: '/finance/getfinanceoverview',
                params: {
                    from_date: date[0],
                    to_date: date[1],
                    scope: SCOPE_TO_API[scope],
                },
            }),
        {
            refreshDeps: [date, scope],
            onError: errorCallBack,
        },
    )

    const combinedData = useMemo(() => (req.data || []).map(getCombinedFinanceOverview), [req.data])

    const summaryData = useMemo(() => {
        const list = combinedData
        if (list.length === 0) return []

        const sum = (key: keyof FinanceOverviewRow) => list.reduce((acc, row) => acc + Number(row[key] || 0), 0)
        const salesIncome = sum('sales_income')
        const orderCount = sum('order_count')
        const rechargeAmount = sum('recharge_amount')
        const refundAmount = sum('refund_amount')
        const totalIncome = list.reduce((acc, row) => acc + getFinanceOverviewTotalIncome(row), 0)

        return [
            { id: 'total', name: '总收款', amount: totalIncome, isMoney: true },
            { id: 'sales', name: '销售额', amount: salesIncome, isMoney: true },
            { id: 'order', name: '订单量', amount: orderCount, isMoney: false },
            {
                id: 'avg',
                name: '客单价',
                amount: orderCount > 0 ? salesIncome / orderCount : 0,
                isMoney: true,
            },
            { id: 'recharge', name: '充值', amount: rechargeAmount, isMoney: true },
            { id: 'refund', name: '订单退款', amount: refundAmount, isMoney: true },
        ]
    }, [combinedData])

    const columns: ColumnsType<FinanceOverviewRow> = [
        {
            title: '时间',
            dataIndex: 'date',
            width: 120,
            render: value => formatDataViewDate(value, scope),
        },
        {
            title: '销售额(元)',
            dataIndex: 'sales_income',
            width: 140,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '商品销售笔数',
            dataIndex: 'order_count',
            width: 120,
            align: 'right',
        },
        {
            title: '客单价(元)',
            width: 120,
            align: 'right',
            render: (_value, row) =>
                row.order_count > 0 ? formatMoney(Number(row.sales_income) / row.order_count) : '-',
        },
        {
            title: '退款金额(元)',
            dataIndex: 'refund_amount',
            width: 140,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '退款笔数',
            dataIndex: 'refund_order_count',
            width: 100,
            align: 'right',
        },
        {
            title: '充值金额(元)',
            dataIndex: 'recharge_amount',
            width: 140,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '充值笔数',
            dataIndex: 'recharge_count',
            width: 100,
            align: 'right',
        },
        {
            title: '余额消费(元)',
            dataIndex: 'consume_amount',
            width: 140,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '总收款(元)',
            width: 140,
            align: 'right',
            render: (_value, row) => formatMoney(getFinanceOverviewTotalIncome(row)),
        },
    ]

    return (
        <Expaned data-column data-overflow-hidden className={cls('content')}>
            <Row data-align-items-center data-margin-top-16 data-justify-content-space-between>
                <Row data-align-items-center>
                    <DataDateSelect
                        onClick={(from, to, nextScope, t) => {
                            setDate([from, to])
                            setScope(nextScope)
                            setTip(t)
                            setShowTip(true)
                        }}
                    />
                </Row>
                <Row data-align-items-center>
                    <div hidden={!showTip} data-color-999 data-font-size-14 data-margin-right-10>
                        {tip}
                    </div>
                    <div className="time-picker">
                        <DatePicker.RangePicker
                            allowClear={false}
                            picker={scopeTypeMap[scope] as any}
                            dateRender={current => (
                                <div className="ant-picker-cell-inner" style={{ borderRadius: '50%' }}>
                                    {current.date()}
                                </div>
                            )}
                            value={[moment(date[0]), moment(date[1])]}
                            onChange={monent => {
                                if (monent) {
                                    setShowTip(false)
                                    if (scopeTypeMap[scope] === 'month') {
                                        const start_date = monent[0]?.format('YYYY-MM-DD')
                                        const end_date = monent[1]
                                            ?.set('date', 1)
                                            .add(1, 'month')
                                            .subtract(1, 'days')
                                            .format('YYYY-MM-DD')
                                        setDate([start_date, end_date])
                                    } else if (scopeTypeMap[scope] === 'year') {
                                        const start_date = monent[0]?.format('YYYY-MM-DD')
                                        const end_date = monent[1]
                                            ?.add(1, 'year')
                                            .set('date', 1)
                                            .set('month', 0)
                                            .subtract(1, 'days')
                                            .format('YYYY-MM-DD')
                                        setDate([start_date, end_date])
                                    } else {
                                        monent[0] && setDate(monent?.map(m => m?.format('YYYY-MM-DD')))
                                    }
                                }
                            }}
                            suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        />
                    </div>
                </Row>
            </Row>
            <Row className={cls('sum')}>
                {summaryData.map(s => (
                    <div key={s.id} className={cls('total')}>
                        <div className={cls('total', 'num')}>
                            {s.isMoney ? '¥' : ''}
                            {s.isMoney ? Number(s.amount).toFixed(2) : s.amount}
                        </div>
                        <div className={cls('total', 'des')}>{s.name}</div>
                    </div>
                ))}
            </Row>
            <Expaned data-overflow-auto className={cls('no-scroll')}>
                <div className={cls('chart')}>
                    <DataViewChart dataList={combinedData} scope={scope} />
                </div>
                <div className={cls('table-wrap')}>
                    <Table
                        rowKey="date"
                        bordered
                        loading={req.loading}
                        columns={columns}
                        dataSource={combinedData}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </Expaned>
        </Expaned>
    )
}

export default DataView
